/**
 * RACKET-LAB-FRONT-1 — RacketLabHome (Jun 2026).
 *
 * Authed Racket Lab launchpad at `/app/racket-lab`. The "app shell"
 * landing the user sees right after login when they choose the Racket
 * Lab app from the TEAM OS Hub.
 *
 * RBAC-filtered module cards: every card includes an explicit `roles`
 * gate so non-admin/non-staff profiles only see what they're supposed
 * to use. Cero DB writes · cero schema apply · all mocks/preview-only
 * modules are flagged accordingly.
 */
import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import useRLContext from "@/hooks/useRLContext";
import RacketLabTopBar from "@/components/layout/RacketLabTopBar";
import RacketLabOnboardingTour, {
  RacketLabOnboardingBanner,
  isOnboardingDone,
  resetOnboarding,
} from "@/components/onboarding/RacketLabOnboardingTour";
import {
  LayoutDashboard, Inbox, CalendarDays, Plus, Boxes, Wrench, ShieldAlert,
  ClipboardList, Tag, FileCheck, Settings as SettingsIcon, ListChecks,
  Sparkles, Lock, UserCircle2,
} from "lucide-react";

const MODULES = [
  // Active for everyone with RL access.
  {
    key: "tickets",
    to: "/app/racket-lab/tickets",
    icon: Inbox,
    accent: "emerald",
    roles: ["user", "admin", "staff", "qa_tester", "qa_manager"],
    status: "active",
  },
  {
    key: "calendar",
    to: "/app/racket-lab/calendar",
    icon: CalendarDays,
    accent: "indigo",
    roles: ["user", "admin", "staff", "qa_tester", "qa_manager"],
    status: "active",
  },
  // Admin-side surfaces · only admin/staff (and QA where applicable).
  {
    key: "dashboard",
    to: "/admin/racket-lab",
    icon: LayoutDashboard,
    accent: "emerald",
    roles: ["admin", "staff", "qa_manager", "qa_tester"],
    status: "admin",
  },
  {
    key: "newTicket",
    to: "/admin/racket-lab/tickets?new=1",
    icon: Plus,
    accent: "amber",
    roles: ["admin", "staff"],
    status: "admin",
  },
  {
    key: "myTickets",
    to: "/admin/racket-lab/my-tickets",
    icon: ListChecks,
    accent: "emerald",
    roles: ["admin", "staff"],
    status: "admin",
  },
  {
    key: "inventory",
    to: "/admin/racket-lab/inventory",
    icon: Boxes,
    accent: "slate",
    roles: ["admin", "staff"],
    status: "admin",
  },
  {
    key: "workforce",
    to: "/admin/racket-lab/operators",
    icon: UserCircle2,
    accent: "slate",
    roles: ["admin", "staff"],
    status: "admin",
  },
  {
    key: "qrTokens",
    to: "/admin/racket-lab/qr-tokens",
    icon: Tag,
    accent: "indigo",
    roles: ["admin", "staff"],
    status: "admin",
  },
  // GEAR / WISHLIST mocks — admin/staff only (Phase 2 documental).
  {
    key: "gearPreview",
    to: "/admin/gear/preview",
    icon: Wrench,
    accent: "emerald",
    roles: ["admin", "staff"],
    status: "mock",
  },
  {
    key: "gearStoreAccess",
    to: "/admin/gear/store-access",
    icon: ShieldAlert,
    accent: "amber",
    roles: ["admin", "staff"],
    status: "mock",
  },
  {
    key: "gearOfferOpportunities",
    to: "/admin/gear/offer-opportunities",
    icon: Inbox,
    accent: "rose",
    roles: ["admin", "staff"],
    status: "mock",
  },
  // Controlled QA profiles (admin/qa_manager only).
  {
    key: "controlledProfiles",
    to: "/admin/racket-lab/controlled-profiles",
    icon: ClipboardList,
    accent: "indigo",
    roles: ["admin", "staff", "qa_manager"],
    status: "qa",
  },
  // Settings — visible for every authed RL user.
  {
    key: "settings",
    to: "/app/racket-lab/settings",
    icon: SettingsIcon,
    accent: "slate",
    roles: ["user", "admin", "staff", "qa_tester", "qa_manager"],
    status: "active",
  },
];

const ACCENTS = {
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50/40", text: "text-emerald-700" },
  amber: { border: "border-amber-200", bg: "bg-amber-50/40", text: "text-amber-700" },
  indigo: { border: "border-indigo-200", bg: "bg-indigo-50/40", text: "text-indigo-700" },
  rose: { border: "border-rose-200", bg: "bg-rose-50/40", text: "text-rose-700" },
  slate: { border: "border-slate-200", bg: "bg-slate-50/40", text: "text-slate-700" },
};

const StatusPill = ({ status, t }) => {
  if (status === "mock") {
    return (
      <span data-testid="rl-card-status-mock" className="rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800">
        {t("racketLabApp.status.mock")}
      </span>
    );
  }
  if (status === "admin") {
    return (
      <span data-testid="rl-card-status-admin" className="rounded-full border border-rose-300 bg-rose-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-rose-800">
        {t("racketLabApp.status.admin")}
      </span>
    );
  }
  if (status === "qa") {
    return (
      <span data-testid="rl-card-status-qa" className="rounded-full border border-indigo-300 bg-indigo-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-800">
        {t("racketLabApp.status.qa")}
      </span>
    );
  }
  return (
    <span data-testid="rl-card-status-active" className="rounded-full border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-800">
      {t("racketLabApp.status.active")}
    </span>
  );
};

const ModuleCard = ({ m, t }) => {
  const Icon = m.icon;
  const accent = ACCENTS[m.accent] || ACCENTS.emerald;
  const title = t(`racketLabApp.modules.${m.key}.title`);
  const desc = t(`racketLabApp.modules.${m.key}.desc`);
  return (
    <Link
      to={m.to}
      data-testid={`rl-home-card-${m.key}`}
      className={`flex flex-col gap-3 rounded-2xl border ${accent.border} ${accent.bg} p-5 transition hover:shadow-md hover:-translate-y-0.5`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={`flex h-11 w-11 items-center justify-center rounded-xl border bg-white/80 ${accent.border} ${accent.text}`}>
          <Icon className="h-5 w-5" />
        </div>
        <StatusPill status={m.status} t={t} />
      </div>
      <div>
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        <p className="mt-1 text-xs text-slate-600">{desc}</p>
      </div>
    </Link>
  );
};

const RacketLabHome = () => {
  const { user } = useAuth();
  const { t } = useLang();
  const { ctx } = useRLContext();
  const role = user?.role;
  // Auto-open the tour on first visit for internal roles. Lazy initial
  // state avoids React Hooks "set-state-in-effect" — the calculation
  // happens once on mount; replay flow remounts the component (via
  // Link navigation back to /app/racket-lab) which re-runs the lazy
  // initializer after resetOnboarding() clears the flag.
  // RL-A11Y-1 (Jun 2026): `?skipTour=1` query param suppresses
  // auto-open for demos/screencasts without persisting any state.
  const [tourOpen, setTourOpen] = useState(() => {
    if (!role) return false;
    if (typeof window !== "undefined") {
      try {
        const params = new URLSearchParams(window.location.search);
        if (params.get("skipTour") === "1") return false;
      } catch (_e) { /* malformed query string · ignore */ }
    }
    const isInternal = ["admin", "staff", "qa_manager", "qa_tester"].includes(role);
    return isInternal && !isOnboardingDone();
  });
  if (!user) return <Navigate to="/login" replace />;
  const visible = MODULES.filter((m) => m.roles.includes(role));
  const onboardingDone = isOnboardingDone();
  const startTour = () => { resetOnboarding(); setTourOpen(true); };
  const closeTour = () => { setTourOpen(false); };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50" data-testid="rl-app-home">
      <RacketLabTopBar />
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6">
        <header className="mb-6" data-testid="rl-home-header">
          <div className="tsw-overline mb-1 text-emerald-700">Racket Lab</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 sm:text-4xl">
            {t("racketLabApp.home.greeting", { name: user.full_name?.split(" ")[0] || user.email })}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            {t("racketLabApp.home.subtitle")}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-slate-500" data-testid="rl-home-identity-strip">
            <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 ring-1 ring-slate-200">
              <UserCircle2 className="h-3 w-3 text-emerald-700" />
              <strong className="text-slate-800">{user.full_name || user.email}</strong>
            </span>
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800">
              {t(`roles.${role}`) || role}
            </span>
            {ctx?.profile && (
              <span className="rounded-full bg-slate-200 px-2 py-0.5 font-medium text-slate-700" data-testid="rl-home-rl-profile">
                {t(`profiles.${ctx.profile}`) || ctx.profile}
              </span>
            )}
            {(ctx?.organisation_id || ctx?.shop_id) && (
              <span className="rounded-full bg-indigo-100 px-2 py-0.5 font-medium text-indigo-800" data-testid="rl-home-rl-scope">
                {ctx.organisation_id}{ctx.shop_id ? ` · ${ctx.shop_id}` : ""}
              </span>
            )}
            <span data-testid="rl-home-onboarding-cta">
              <RacketLabOnboardingBanner
                onStart={startTour}
                label={onboardingDone ? t("racketLabApp.onboarding.replayBanner") : t("racketLabApp.onboarding.startBanner")}
              />
            </span>
          </div>
        </header>

        <section data-testid="rl-home-modules-grid" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((m) => (
            <ModuleCard key={m.key} m={m} t={t} />
          ))}
        </section>

        {visible.length === 0 && (
          <div data-testid="rl-home-empty" className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900">
            <Lock className="mr-1 inline h-3.5 w-3.5" />
            {t("racketLabApp.home.emptyAccess")}
          </div>
        )}

        <footer className="mt-8 rounded-lg border border-slate-200 bg-white p-3 text-[11px] italic text-slate-600" data-testid="rl-home-footer-legal">
          <Sparkles className="mr-1 inline h-3 w-3 text-emerald-700" />
          {t("racketLabApp.home.footerLegal")}
        </footer>
      </main>
      <RacketLabOnboardingTour key={tourOpen ? "open" : "closed"} open={tourOpen} onClose={closeTour} moduleSteps={visible} />
    </div>
  );
};

export default RacketLabHome;
